// Import necessary libraries and data
import { useState, useEffect } from 'react'
import axios from 'axios'
import { useRouter } from 'next/router'
import { useForm } from 'react-hook-form'
import { PrismicRichText } from '@prismicio/react'
import currentMemberSmsData from '@data/currentMemberSms.json'

// Define the CurrentMemberSms component
export default function CurrentMemberSms() {
	// Initialize router for getting query parameters
	const router = useRouter()

	// Initialize state variables
	const [urlEmail, setUrlEmail] = useState('')
	const [email, setEmail] = useState('')
	const [phone, setPhone] = useState('')
	const [countryCode, setCountryCode] = useState('')
	const [subscriptionGroupId] = useState(currentMemberSmsData.braze_subscription_group[0].text)
	const [eventName] = useState(currentMemberSmsData.braze_custom_event_name[0].text)
	const [isSubmitted, setIsSubmitted] = useState(false)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [imageUrl, setImageUrl] = useState('')

	// Set country code based on the host
	useEffect(() => {
		if (window.location.host.includes('.co.uk')) {
			setCountryCode('GB')
		} else {
			setCountryCode('US')
		}
	}, [])

	// Set image URL based on the viewport width
	useEffect(() => {
		if (typeof window !== 'undefined') {
			if (window.innerWidth <= 600) {
				setImageUrl(currentMemberSmsData?.background_image_mobile?.url)
			} else {
				setImageUrl(currentMemberSmsData?.background_image?.url)
			}
		}
	}, [])

	// Set email from the query parameter
	useEffect(() => {
		if (router.query.email) {
			setUrlEmail(router.query.email as string)
		}
	}, [router.query.email])

	// Define form inputs
	type FormInputs = {
		signUpModalEmail: string
		signUpModalPhone: string
	}

	// Initialize form handling
	const { handleSubmit } = useForm<FormInputs>({
		mode: 'onChange',
	})

	// Define form submission function
	const onSubmit = () => {
		sendDataToServer()
	}

	// Define function for sending data to server. This sends braze the data from the server and checks for duplicates
	const sendDataToServer = () => {
		const emailToSend = urlEmail || email
		const subscribeType = 'sms'

		setIsSubmitting(true)

		axios
			.post('/api/braze/sms-opt-in', {
				email: emailToSend,
				phone,
				subscribeType,
				subscriptionGroupId,
				countryCode,
				eventName
			})
			.then(response => {
				console.log(response.data)
				setIsSubmitted(true)
			})
			.catch((error) => console.error('Error:', error))
			.finally(() => setIsSubmitting(false))
	}

	return (
		/* eslint-disable jsx-a11y/label-has-associated-control */
		/* The is some potential issue with eslint where it is not reconizing htmlfor inside the logic of isSubmitted.  I added the disable for this reason. */
		<section id="current_member_sms" style={{ backgroundImage: `url(${imageUrl})`, backgroundSize: 'cover' }} className="pb-[16vh] pt-[8vh]">
			<div>
				<div className="container flex justify-center">
					<div className="content-wrapper flex flex-col justify-center items-center text-center px-10 lg:px-14 md:px-14 max-w-[500px] py-14 lg:py-16 bg-white rounded shadow-bbx-lg border border-gray-200 mx-1.5">
						{!isSubmitted ? (
							<div id="form_data">
								<h1 className="text-2xl font-medium md:text-3xl mb-4">{currentMemberSmsData.title[0].text}</h1>
								<p className="justify-center mb-6">{currentMemberSmsData.description[0].text}</p>
								<form onSubmit={handleSubmit(onSubmit)} className="w-full mb-4">
									<div className="form-control mb-2">
										<input id="signUpModalPhone" required className={`input ${phone ? 'is-filled' : ''}`} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} pattern="^\d{10}$" title="Please enter a valid 10-digit phone number." />
										<label htmlFor="signUpModalPhone" className="label">Phone Number</label>
									</div>
									{urlEmail ? (
										<input id="signUpModalEmail" type="hidden" value={urlEmail} />
									) : (
										<div id="customer_email_address" className="mb-4">
											<div className="form-control">
												<input id="signUpModalEmail" required className={`input ${email ? 'is-filled' : ''}`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
												<label htmlFor="signUpModalEmail" className="label">Email</label>
											</div>
											<small className="bg-gray-150 p-4 block text-gray-500 text-left">Email address is used to connect your phone number to your subscription account</small>
										</div>
									)}

									<button type="submit" className="button button-primary w-full" disabled={isSubmitting}>{isSubmitting ? 'Sending...' : 'Submit'}</button>
								</form>
								<small className="text-left">
									<PrismicRichText field={currentMemberSmsData.small_print as any} />
								</small>
							</div>
						) : (
							<div id="form_success">
								<h1 className="text-2xl font-medium md:text-3xl mb-8">{currentMemberSmsData.confirmation_header[0].text}!</h1>
								<div className="mb-8">
									<svg className="mobile-phone-icon" width="62" height="128" viewBox="0 0 62 128" aria-labelledby="phoneSvgLabel" aria-hidden="true" fill="#000000" role="img" data-attn-element-id="E5vE">
										<path fillRule="nonzero" d="M53.496 0C58.193 0 61.999 3.831 62 8.559v110.884c0 4.726-3.806 8.557-8.503 8.557H8.503C3.806 128 0 124.169 0 119.443V8.557C0 3.831 3.806 0 8.503 0zM15.759 3.813h-5.603c-3.192 0-5.78 2.604-5.78 5.816V117.88c0 3.212 2.588 5.816 5.78 5.816h41.613c3.192 0 5.78-2.604 5.78-5.816V9.63c0-3.212-2.587-5.816-5.779-5.816h-4.645c-.204.317-.237.786-.242 1.17v.303c0 .623-.885 2.198-2.348 2.377-1.392.169-12.006.017-13.092.001-1.088.016-11.703.168-13.09-.001-1.467-.179-2.351-1.754-2.351-2.377 0-.408.021-1.065-.243-1.473zM48 16a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H14a2 2 0 0 1-2-2V18a2 2 0 0 1 2-2h34zm-3 10H17a1 1 0 0 0-.993.883L16 27v2a1 1 0 0 0 .883.993L17 30h28a1 1 0 0 0 .993-.883L46 29v-2a1 1 0 0 0-1-1zm-18-6H17a1 1 0 0 0-.993.883L16 21v2a1 1 0 0 0 .883.993L17 24h10a1 1 0 0 0 .993-.883L28 23v-2a1 1 0 0 0-1-1z"/>
									</svg>
								</div>
								<p className="justify-center mb-6">{currentMemberSmsData.confirmation_summary[0].text}</p>
							</div>
						)}
					</div>
				</div>
			</div>
		</section>
	)
}